/**
 * CMSWysiwygEditor — TinyMCE (self-hosted) visual editor for CMS page bodies.
 * Keeps HTML output and allows custom elements/classes used by themed sections.
 */

import { useRef } from "react";
import { Editor } from "@tinymce/tinymce-react";
import type { Editor as TinyMCEEditor, EditorEvent } from "tinymce";
import "tinymce/tinymce";
import "tinymce/icons/default";
import "tinymce/models/dom";
import "tinymce/themes/silver";
import "tinymce/plugins/advlist";
import "tinymce/plugins/anchor";
import "tinymce/plugins/autolink";
import "tinymce/plugins/charmap";
import "tinymce/plugins/code";
import "tinymce/plugins/fullscreen";
import "tinymce/plugins/help";
import "tinymce/plugins/image";
import "tinymce/plugins/link";
import "tinymce/plugins/lists";
import "tinymce/plugins/media";
import "tinymce/plugins/nonbreaking";
import "tinymce/plugins/preview";
import "tinymce/plugins/quickbars";
import "tinymce/plugins/searchreplace";
import "tinymce/plugins/table";
import "tinymce/plugins/visualblocks";
import "tinymce/plugins/wordcount";
import "tinymce/skins/ui/oxide/skin.min.css";
import "tinymce/skins/content/default/content.min.css";
import { CMS_PLACEHOLDER_KEYS } from "@/lib/cms-placeholders";
import {
	CMS_BLOCK_DRAG_MIME,
	CMS_EDITOR_BLOCKS,
} from "@/lib/cms-editor-blocks";
import { CMSComponentLibrarySidebar } from "./CMSComponentLibrarySidebar";

interface CMSWysiwygEditorProps {
	value: string;
	onChange: (html: string) => void;
	placeholder?: string;
	className?: string;
}

const EMBED_INSERTIONS = [
	{
		label: "Embed: FAQ section",
		value: '<div data-gsgo-embed="faq-section"></div>',
	},
	{
		label: "Embed: Blog articles",
		value: '<div data-gsgo-embed="blog-articles"></div>',
	},
	{
		label: "Embed: Featured blog articles",
		value: '<div data-gsgo-embed="blog-articles-featured"></div>',
	},
] as const;

/** Known CMS block section classes — fallback if custom MIME is missing after drag. */
const CMS_BLOCK_CLASS_MARKERS = [
	"cms-block-blank-section",
	"cms-block-cards",
	"cms-block-split",
	"cms-block-cta",
	"cms-block-quote",
] as const;

function isBlankSectionHtml(html: string): boolean {
	return html.includes("cms-block-blank-section");
}

function isSectionBlockHtml(html: string): boolean {
	return /^\s*<section\b/i.test(html);
}

function getSectionRoot(doc: Document): HTMLElement {
	return (doc.querySelector("main") as HTMLElement | null) ?? doc.body;
}

function getTopLevelSectionFromNode(
	doc: Document,
	node: Node | null,
): HTMLElement | null {
	const root = getSectionRoot(doc);
	if (!node) return null;
	let el: HTMLElement | null =
		node.nodeType === Node.ELEMENT_NODE
			? (node as HTMLElement)
			: (node.parentElement as HTMLElement | null);
	while (el && el !== root) {
		if (
			el.tagName === "SECTION" &&
			el.parentElement === root &&
			!el.hasAttribute("data-mce-bogus")
		) {
			return el;
		}
		el = el.parentElement as HTMLElement | null;
	}
	return null;
}

function findNearestSectionFromNode(
	doc: Document,
	node: Node | null,
): HTMLElement | null {
	return getTopLevelSectionFromNode(doc, node);
}

function insertSectionBlockAtDrop(
	editor: TinyMCEEditor,
	html: string,
	clientX: number,
	clientY: number,
	dropTarget: Node | null,
): void {
	const doc = editor.getDoc();
	const root = getSectionRoot(doc);

	editor.undoManager.transact(() => {
		const sectionFromTarget = findNearestSectionFromNode(doc, dropTarget);
		const sectionFromPoint = findNearestSectionFromNode(
			doc,
			doc.elementFromPoint(clientX, clientY),
		);
		const anchor = sectionFromTarget ?? sectionFromPoint;
		if (!anchor) {
			root.insertAdjacentHTML("beforeend", html);
			return;
		}

		const rect = anchor.getBoundingClientRect();
		const insertAfter = clientY >= rect.top + rect.height / 2;
		anchor.insertAdjacentHTML(insertAfter ? "afterend" : "beforebegin", html);
	});

	editor.nodeChanged();
}

function getCaretRangeFromClientPoint(
	doc: Document,
	clientX: number,
	clientY: number,
): Range | null {
	const d = doc as Document & {
		caretRangeFromPoint?: (x: number, y: number) => Range | null;
		caretPositionFromPoint?: (
			x: number,
			y: number,
		) => { offsetNode: Node; offset: number } | null;
	};
	if (typeof d.caretRangeFromPoint === "function") {
		try {
			return d.caretRangeFromPoint(clientX, clientY);
		} catch {
			return null;
		}
	}
	if (typeof d.caretPositionFromPoint === "function") {
		const pos = d.caretPositionFromPoint(clientX, clientY);
		if (pos?.offsetNode) {
			const range = doc.createRange();
			range.setStart(pos.offsetNode, pos.offset);
			range.collapse(true);
			return range;
		}
	}
	return null;
}

interface DropZone {
	type: "before" | "after" | "inside" | "append";
	element?: Element;
}

function getTopLevelChildFromNode(doc: Document, node: Node | null): Element | null {
	const root = getSectionRoot(doc);
	if (!node) return null;
	let el: Element | null =
		node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement;
	while (el && el.parentElement && el.parentElement !== root) {
		el = el.parentElement;
	}
	if (!el || el === root || el.hasAttribute("data-mce-bogus")) {
		return null;
	}
	return el;
}

/**
 * Determines where to insert based on drop coordinates.
 * Returns zone info for visual feedback and insertion.
 */
function getDropZone(
	doc: Document,
	clientX: number,
	clientY: number,
	dropTarget?: Node | null,
): DropZone | null {
	const root = getSectionRoot(doc);
	const topLevelSections = Array.from(root.querySelectorAll(":scope > section")).filter(
		(el) => !el.hasAttribute("data-mce-bogus"),
	);
	const topLevelChildren = (
		topLevelSections.length > 0 ? topLevelSections : Array.from(root.children)
	).filter(
		(el) => !el.hasAttribute("data-mce-bogus") && el.nodeType === 1,
	);

	// Empty editor → append
	if (topLevelChildren.length === 0) {
		return { type: "append" };
	}

	const firstTopLevel = topLevelChildren[0] as Element;
	const lastTopLevel = topLevelChildren[topLevelChildren.length - 1] as Element;
	const firstRect = firstTopLevel.getBoundingClientRect();
	const lastRect = lastTopLevel.getBoundingClientRect();

	// Above first element → before first
	if (clientY < firstRect.top + 8) {
		return { type: "before", element: firstTopLevel };
	}

	// Below last element → after last
	if (clientY > lastRect.bottom - 8) {
		return { type: "after", element: lastTopLevel };
	}

	// Prefer target node first; fallback to point hit-test.
	const topLevel =
		getTopLevelChildFromNode(doc, dropTarget ?? null) ??
		getTopLevelChildFromNode(doc, doc.elementFromPoint(clientX, clientY));

	if (!topLevel) {
		return { type: "append" };
	}

	const rect = topLevel.getBoundingClientRect();
	if (rect.height <= 0) {
		return null;
	}

	const relativeY = clientY - rect.top;
	const threshold = Math.min(40, rect.height * 0.2); // 20% or 40px, whichever is smaller

	if (relativeY < threshold) {
		return { type: "before", element: topLevel };
	}
	if (relativeY > rect.height - threshold) {
		return { type: "after", element: topLevel };
	}

	return { type: "inside", element: topLevel };
}

/**
 * Inserts HTML at the specified drop zone.
 */
function insertAtDropZone(
	editor: TinyMCEEditor,
	zone: DropZone,
	html: string,
	clientX: number,
	clientY: number,
): void {
	const doc = editor.getDoc();
	const rng = doc.createRange();

	try {
		const forceTopLevelSibling = isBlankSectionHtml(html) || isSectionBlockHtml(html);
		if (forceTopLevelSibling && zone.type === "inside" && zone.element) {
			rng.setStartAfter(zone.element);
			rng.collapse(true);
			editor.selection.setRng(rng);
			editor.focus();
			editor.insertContent(html);
			return;
		}

		if (zone.type === "before" && zone.element) {
			rng.setStartBefore(zone.element);
			rng.collapse(true);
			editor.selection.setRng(rng);
		} else if (zone.type === "after" && zone.element) {
			rng.setStartAfter(zone.element);
			rng.collapse(true);
			editor.selection.setRng(rng);
		} else if (zone.type === "append") {
			rng.selectNodeContents(doc.body);
			rng.collapse(false);
			editor.selection.setRng(rng);
		} else if (zone.type === "inside") {
			// For inside drops, try to position caret at drop point
			const caretRng = getCaretRangeFromClientPoint(doc, clientX, clientY);
			if (caretRng) {
				editor.selection.setRng(caretRng);
			}
			// Otherwise just insert at current selection
		}

		editor.focus();
		editor.insertContent(html);
	} catch (err) {
		console.error("[CMSWysiwygEditor] insertAtDropZone error:", err);
	}
}

/**
 * For section inserts from "+" button/menu, force top-level sibling insertion.
 */
function insertSectionAsTopLevel(editor: TinyMCEEditor, html: string): void {
	const doc = editor.getDoc();
	const root = getSectionRoot(doc);

	editor.undoManager.transact(() => {
		const anchor = getTopLevelSectionFromNode(doc, editor.selection.getNode());
		if (anchor) {
			anchor.insertAdjacentHTML("afterend", html);
		} else {
			root.insertAdjacentHTML("beforeend", html);
		}
	});

	editor.nodeChanged();
}

/**
 * Shows visual drop indicator.
 */
function showDropIndicator(doc: Document, zone: DropZone): void {
	removeDropIndicator(doc);

	const indicator = doc.createElement("div");
	indicator.setAttribute("data-drop-indicator", "true");
	indicator.style.cssText =
		"position: absolute; left: 0; right: 0; height: 3px; background: #3b82f6; pointer-events: none; z-index: 9999; box-shadow: 0 0 6px rgba(59, 130, 246, 0.6);";

	if (zone.type === "before" && zone.element) {
		const rect = zone.element.getBoundingClientRect();
		const scrollY = doc.documentElement.scrollTop || doc.body.scrollTop;
		indicator.style.top = `${rect.top + scrollY - 2}px`;
		doc.body.appendChild(indicator);
	} else if (zone.type === "after" && zone.element) {
		const rect = zone.element.getBoundingClientRect();
		const scrollY = doc.documentElement.scrollTop || doc.body.scrollTop;
		indicator.style.top = `${rect.bottom + scrollY - 1}px`;
		doc.body.appendChild(indicator);
	} else if (zone.type === "append") {
		const body = doc.body;
		const scrollY = doc.documentElement.scrollTop || doc.body.scrollTop;
		const bodyRect = body.getBoundingClientRect();
		indicator.style.top = `${bodyRect.bottom + scrollY}px`;
		doc.body.appendChild(indicator);
	}
}

function removeDropIndicator(doc: Document): void {
	const existing = doc.querySelector("[data-drop-indicator]");
	existing?.remove();
}

export function CMSWysiwygEditor({
	value,
	onChange,
	placeholder = "Write your page content here…",
	className,
}: Readonly<CMSWysiwygEditorProps>): JSX.Element {
	const editorRef = useRef<TinyMCEEditor | null>(null);

	const insertHtml = (html: string): void => {
		const ed = editorRef.current;
		if (!ed) return;
		if (isSectionBlockHtml(html)) {
			insertSectionAsTopLevel(ed, html);
		} else {
			ed.focus();
			ed.insertContent(html);
		}
		onChange(ed.getContent());
	};

	return (
		<div
			className={`h-full min-h-0 border border-gray-300 rounded-lg overflow-hidden bg-white flex flex-col ${className ?? ""}`}
		>
			<div className="flex-1 min-h-0 flex flex-row overflow-hidden">
				<div className="flex-1 min-w-0 min-h-0 flex flex-col">
					<Editor
						licenseKey="gpl"
						onInit={(_, editor) => {
							editorRef.current = editor;
						}}
						value={value}
						onEditorChange={(content) => onChange(content)}
						init={{
							height: "100%",
							inline: false,
							menubar:
								"edit view insert format table tools help cmsPlaceholders cmsEmbeds cmsSections",
							plugins: [
								"advlist",
								"anchor",
								"autolink",
								"charmap",
								"code",
								"fullscreen",
								"help",
								"image",
								"link",
								"lists",
								"media",
								"nonbreaking",
								"preview",
								"quickbars",
								"searchreplace",
								"table",
								"visualblocks",
								"wordcount",
							],
							toolbar:
							"undo redo | blocks fontfamily fontsize | forecolor backcolor | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image media table | removeformat code preview fullscreen visualblocks",
							setup: (editor: TinyMCEEditor) => {
								const syncParent = (): void => {
									onChange(editor.getContent());
								};

								let currentDropZone: DropZone | null = null;

								editor.on(
									"dragover",
									(e: EditorEvent<DragEvent>) => {
										const dt = e.dataTransfer;
										if (!dt) return;

										// Check if this is a block palette drag
										const types = Array.from(dt.types || []);
										const hasBlockMime =
											types.includes(CMS_BLOCK_DRAG_MIME) ||
											types.includes("text/html");

										if (!hasBlockMime) return;

										e.preventDefault();
										dt.dropEffect = "copy";

										// Show drop indicator
										const doc = editor.getDoc();
										const win = editor.getWin();
										if (!win) return;

										// TinyMCE coordinates are already relative to the iframe document
										const zone = getDropZone(
											doc,
											e.clientX,
											e.clientY,
											e.target as Node,
										);
										if (zone && JSON.stringify(zone) !== JSON.stringify(currentDropZone)) {
											currentDropZone = zone;
											showDropIndicator(doc, zone);
										}
									},
									true,
								);

								editor.on(
									"dragleave",
									() => {
										const doc = editor.getDoc();
										removeDropIndicator(doc);
										currentDropZone = null;
									},
									true,
								);

								editor.on(
									"drop",
									(e: EditorEvent<DragEvent>) => {
										const dt = e.dataTransfer;
										const doc = editor.getDoc();
										removeDropIndicator(doc);
										currentDropZone = null;

										if (!dt || dt.files.length > 0) {
											return;
										}

										const blockId = dt.getData(CMS_BLOCK_DRAG_MIME);
										const html = (
											dt.getData("text/html") ||
											dt.getData("text/plain") ||
											""
										).trim();
										if (!html) {
											return;
										}

										const hasClassMarker = CMS_BLOCK_CLASS_MARKERS.some((c) =>
											html.includes(c),
										);
										const isPaletteDrag =
											blockId.length > 0 ||
											(html.startsWith("<section") && hasClassMarker);

										if (!isPaletteDrag) {
											return;
										}

										e.preventDefault();
										e.stopPropagation();
										e.stopImmediatePropagation();

										if (isSectionBlockHtml(html)) {
											insertSectionBlockAtDrop(
												editor,
												html,
												e.clientX,
												e.clientY,
												e.target as Node,
											);
											syncParent();
											return;
										}

										// TinyMCE coordinates are already relative to the iframe document
										const zone = getDropZone(
											doc,
											e.clientX,
											e.clientY,
											e.target as Node,
										);
										if (zone) {
											insertAtDropZone(editor, zone, html, e.clientX, e.clientY);
											syncParent();
										}
									},
									true,
								);

								for (const key of CMS_PLACEHOLDER_KEYS) {
									editor.ui.registry.addMenuItem(`cmsPlaceholder_${key}`, {
										text: `{{${key}}}`,
										onAction: () => {
											editor.focus();
											editor.insertContent(`{{${key}}}`);
											syncParent();
										},
									});
								}

								for (const embed of EMBED_INSERTIONS) {
									const embedId = embed.label
										.toLowerCase()
										.replace(/[^a-z0-9]+/g, "_");
									editor.ui.registry.addMenuItem(`cmsEmbed_${embedId}`, {
										text: embed.label,
										onAction: () => {
											editor.focus();
											editor.insertContent(embed.value);
											syncParent();
										},
									});
								}

								for (const block of CMS_EDITOR_BLOCKS) {
									editor.ui.registry.addMenuItem(`cmsSection_${block.id}`, {
										text: block.title,
										onAction: () => {
											if (isSectionBlockHtml(block.html)) {
												insertSectionAsTopLevel(editor, block.html);
											} else {
												editor.focus();
												editor.insertContent(block.html);
											}
											syncParent();
										},
									});
								}
							},
							menu: {
								cmsPlaceholders: {
									title: "Placeholders",
									items: CMS_PLACEHOLDER_KEYS.map(
										(key) => `cmsPlaceholder_${key}`,
									).join(" "),
								},
								cmsEmbeds: {
									title: "Embeds",
									items: EMBED_INSERTIONS.map((embed) => {
										const embedId = embed.label
											.toLowerCase()
											.replace(/[^a-z0-9]+/g, "_");
										return `cmsEmbed_${embedId}`;
									}).join(" "),
								},
								cmsSections: {
									title: "Sections",
									items: CMS_EDITOR_BLOCKS.map((b) => `cmsSection_${b.id}`).join(
										" ",
									),
								},
							},
							quickbars_selection_toolbar:
								"bold italic underline forecolor backcolor | h2 h3 blockquote | link",
							quickbars_insert_toolbar:
								"quickimage quicktable | hr",
							contextmenu: "link image table",
							resize: false,
							placeholder,
							branding: false,
							promotion: false,
							object_resizing: true,
							relative_urls: false,
							remove_script_host: false,
							convert_urls: false,
							entity_encoding: "raw",
							extended_valid_elements:
								"section[*],article[*],main[*],header[*],footer[*],div[*],span[*],a[*],img[*],iframe[*],video[*],source[*],svg[*],path[*],style[*],script[*]",
							valid_elements: "*[*]",
							valid_children:
								"+body[style|section|article|header|footer|main|div],+section[section|article|div],+article[section|div],+div[section|article|div]",
							valid_classes: { "*": "*" },
							content_css: ["/src/index.css"],
							content_style:
								"body { margin: 0; padding: 16px; } .mce-content-body { font-family: inherit; }",
						}}
					/>
				</div>

				<CMSComponentLibrarySidebar
					blocks={CMS_EDITOR_BLOCKS}
					onInsert={insertHtml}
				/>
			</div>

			<div className="shrink-0 border-t border-gray-200 bg-gray-50 px-3 py-2 text-[11px] text-gray-500">
				<strong className="font-medium text-gray-600">Tip:</strong> Drag blocks from the right panel. Drop at <strong>top/bottom edge</strong> of a section to insert before/after, drop in the <strong>middle</strong> to insert inside, or drop in the <strong>blank area below</strong> to append a new section.
			</div>
		</div>
	);
}
